import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface UnmappedLender {
  lender_string: string;
  occurrences?: number;
  loan_count?: number;
}

export type AdviserRole =
  | 'broker'
  | 'operations'
  | 'marketing'
  | 'management'
  | 'general'
  | 'inactive';

export interface AdviserRow {
  adviser_id: string;
  email: string | null;
  adviser_name: string | null;
  valutten_broker_ids: string[];
  adviser_role: AdviserRole | null;
  deal_count: number;
}

export interface InstitutionRow {
  institution_key: string;
  display_name: string;
  category: string;
}

export interface BrokerCandidate {
  broker_display: string;
  broker_id: string;
  loans: number;
}

export interface MappingsData {
  unmappedLenders: {
    mycrm: UnmappedLender[];
    raw_commissions: UnmappedLender[];
  };
  advisers: AdviserRow[];
  institutions: InstitutionRow[];
  brokerCandidates: BrokerCandidate[];
}

export type LenderSource = 'mycrm' | 'raw_commissions' | 'afg_template' | 'connective_template' | 'other';

interface UseCrmMappingsState {
  data: MappingsData | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  saveLenderAlias: (args: {
    source: LenderSource;
    lender_string: string;
    institution_key: string;
  }) => Promise<void>;
  saveAdviserBrokerIds: (args: {
    adviser_id: string;
    broker_display_names: string[];
  }) => Promise<{ computed_broker_ids: string[] }>;
  saveAdviserRole: (args: {
    adviser_id: string;
    role: AdviserRole | null;
  }) => Promise<void>;
  resetSyncWatermark: (args: {
    connectionId: string;
  }) => Promise<{ before: Record<string, unknown> | null }>;
}

export function useCrmMappings(companyId: string | undefined): UseCrmMappingsState {
  const [data, setData] = useState<MappingsData | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    const fn = httpsCallable<
      { action: 'getMappingsData'; companyId: string },
      MappingsData
    >(functions, 'crmMappingAction');
    setLoading(true);
    setError(null);
    fn({ action: 'getMappingsData', companyId })
      .then((r) => setData(r.data))
      .catch((err) => {
        console.error('[useCrmMappings] fetch failed', err);
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
  }, [companyId, refreshKey]);

  const saveLenderAlias = useCallback(
    async (args: {
      source: LenderSource;
      lender_string: string;
      institution_key: string;
    }) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          action: 'saveLenderAlias';
          companyId: string;
          source: LenderSource;
          lender_string: string;
          institution_key: string;
        },
        { success: boolean; lender_string_normalized: string }
      >(functions, 'crmMappingAction');
      await fn({ action: 'saveLenderAlias', companyId, ...args });
      setRefreshKey((k) => k + 1);
    },
    [companyId]
  );

  const saveAdviserBrokerIds = useCallback(
    async (args: { adviser_id: string; broker_display_names: string[] }) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          action: 'saveAdviserBrokerIds';
          companyId: string;
          adviser_id: string;
          broker_display_names: string[];
        },
        { success: boolean; computed_broker_ids: string[] }
      >(functions, 'crmMappingAction');
      const result = await fn({ action: 'saveAdviserBrokerIds', companyId, ...args });
      setRefreshKey((k) => k + 1);
      return { computed_broker_ids: result.data.computed_broker_ids };
    },
    [companyId]
  );

  const saveAdviserRole = useCallback(
    async (args: { adviser_id: string; role: AdviserRole | null }) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          action: 'saveAdviserRole';
          companyId: string;
          adviser_id: string;
          role: AdviserRole | null;
        },
        { success: boolean; role: AdviserRole | null }
      >(functions, 'crmMappingAction');
      await fn({ action: 'saveAdviserRole', companyId, ...args });
      setRefreshKey((k) => k + 1);
    },
    [companyId]
  );

  const resetSyncWatermark = useCallback(
    async (args: { connectionId: string }) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          action: 'resetSyncWatermark';
          companyId: string;
          connectionId: string;
        },
        {
          success: boolean;
          connectionId: string;
          before: Record<string, unknown> | null;
        }
      >(functions, 'crmMappingAction');
      const result = await fn({
        action: 'resetSyncWatermark',
        companyId,
        connectionId: args.connectionId,
      });
      return { before: result.data.before };
    },
    [companyId]
  );

  return {
    data,
    loading,
    error,
    refresh,
    saveLenderAlias,
    saveAdviserBrokerIds,
    saveAdviserRole,
    resetSyncWatermark,
  };
}
